/**
 * Reference data for onboarding dropdowns. Centralised so the frontend
 * components and the backend schemas stay in lockstep — when adding a new
 * value, the corresponding `enum` in /app/backend/validators.py STEP_SCHEMAS
 * must include the same string. Tests assert the lists overlap.
 */

// Primary contact / director role pick list. "Other" is the escape hatch —
// when selected, callers can capture the actual role in a free-text field
// (out of scope for this pass; today "Other" is recorded as-is).
export const ROLE_OPTIONS = [
  "CEO",
  "CFO",
  "CTO",
  "COO",
  "Director",
  "Founder",
  "Compliance Officer",
  "Legal Counsel",
  "Finance",
  "Operations",
  "Head of Payments",
  "Other",
];

// Underwriting verticals — aligned with policy categories.
export const VERTICAL_OPTIONS = [
  "E-commerce — physical goods",
  "E-commerce — digital goods",
  "SaaS / subscriptions",
  "Marketplace",
  "Travel & hospitality",
  "Gaming & gambling",
  "Crypto & digital assets",
  "Financial services",
  "Health, beauty & wellness",
  "Education",
  "Charity / non-profit",
  "Professional services",
  "Other",
];

// Settlement currencies the platform supports today.
export const CURRENCY_OPTIONS = ["EUR", "USD", "GBP", "JPY", "TRY"];

// Full ISO 3166-1 country list — short English names. Country choice is
// a fact about reality (where someone is incorporated, where a director
// was born) — NOT a business filter. Hard-blocked / unsupported
// jurisdictions are enforced at acceptance time via the cross-check
// engine and underwriting policy, NOT by hiding countries from the
// form. A merchant incorporated in a sanctioned country must still be
// able to truthfully state that — we reject at review, not at form-fill.
//
// 249 entries: 193 UN member states + 2 observer states (Holy See,
// Palestine) + Taiwan + ~53 commonly-used territories/dependencies.
// MUST mirror backend `validators.COUNTRY_OPTIONS` exactly — drift
// check on boot would surface mismatches.
export const COUNTRY_OPTIONS = [
  "Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa",
  "Andorra", "Angola", "Anguilla", "Antarctica", "Antigua and Barbuda",
  "Argentina", "Armenia", "Aruba", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia",
  "Bonaire, Sint Eustatius and Saba",
  "Bosnia and Herzegovina", "Botswana", "Bouvet Island", "Brazil",
  "British Indian Ocean Territory", "Brunei Darussalam", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon",
  "Canada", "Cayman Islands", "Central African Republic", "Chad",
  "Chile", "China", "Christmas Island", "Cocos (Keeling) Islands",
  "Colombia", "Comoros", "Congo", "Congo, Democratic Republic of the",
  "Cook Islands", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba",
  "Curaçao", "Cyprus", "Czechia", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador",
  "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France",
  "French Guiana", "French Polynesia", "French Southern Territories",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar",
  "Greece", "Greenland", "Grenada", "Guadeloupe", "Guam", "Guatemala",
  "Guernsey", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
  "Heard Island and McDonald Islands", "Holy See", "Honduras",
  "Hong Kong", "Hungary", "Iceland", "India", "Indonesia",
  "Iran", "Iraq", "Ireland", "Isle of Man", "Israel", "Italy",
  "Jamaica", "Japan", "Jersey", "Jordan", "Kazakhstan", "Kenya",
  "Kiribati", "Korea, North", "Korea, South", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Macao", "Madagascar",
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
  "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Montserrat", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru",
  "Nepal", "Netherlands", "New Caledonia", "New Zealand", "Nicaragua",
  "Niger", "Nigeria", "Niue", "Norfolk Island", "North Macedonia",
  "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau",
  "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
  "Philippines", "Pitcairn", "Poland", "Portugal", "Puerto Rico",
  "Qatar", "Réunion", "Romania", "Russia", "Rwanda",
  "Saint Barthélemy", "Saint Helena, Ascension and Tristan da Cunha",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Martin (French part)",
  "Saint Pierre and Miquelon", "Saint Vincent and the Grenadines",
  "Samoa", "San Marino", "São Tomé and Príncipe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
  "Sint Maarten (Dutch part)", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Georgia and the South Sandwich Islands",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
  "Svalbard and Jan Mayen", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste",
  "Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia",
  "Turkey", "Turkmenistan", "Turks and Caicos Islands", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "United States Minor Outlying Islands", "Uruguay",
  "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam",
  "Virgin Islands (British)", "Virgin Islands (U.S.)",
  "Wallis and Futuna", "Western Sahara", "Yemen", "Zambia", "Zimbabwe",
];

// Merchant-size buckets — Step 1.
export const EMPLOYEES_RANGES = [
  "1-10", "11-50", "51-200", "201-500", "501-1,000", "1,000+",
];

// Annual revenue buckets — Step 1. EUR-denominated; merchants outside the
// eurozone should approximate.
export const ANNUAL_TURNOVER_RANGES = [
  "< €100k",
  "€100k - €500k",
  "€500k - €1M",
  "€1M - €5M",
  "€5M - €25M",
  "€25M - €100M",
  "€100M+",
];

// Step 5 — processing volume & transaction-value buckets.
export const MONTHLY_VOLUME_RANGES = [
  "< €10k",
  "€10k - €50k",
  "€50k - €250k",
  "€250k - €1M",
  "€1M - €5M",
  "€5M - €25M",
  "€25M+",
];

export const AVERAGE_TXN_VALUE_RANGES = [
  "< €25",
  "€25 - €100",
  "€100 - €500",
  "€500 - €2,000",
  "€2,000 - €10,000",
  "€10,000+",
];

export const CHARGEBACK_RATE_RANGES = [
  "< 0.5%",
  "0.5% - 1%",
  "1% - 2%",
  "2%+",
];

export const REFUND_RATE_RANGES = [
  "< 1%",
  "1% - 5%",
  "5% - 10%",
  "10%+",
];

// Step 3 / Step 5 — multi-select pick lists. Frontend renders a checkbox
// grid; backend enforces `item_enum` on the array.
export const SALES_CHANNELS = [
  "Online store / website",
  "Mobile app",
  "Marketplace (Amazon, eBay, Etsy, etc.)",
  "In-person / retail POS",
  "Phone / mail order",
  "B2B / sales team",
  "Other",
];

export const PAYMENT_METHODS = [
  "Visa",
  "Mastercard",
  "American Express",
  "Apple Pay",
  "Google Pay",
  "Bank transfer / SEPA",
  "Buy now, pay later (BNPL)",
  "Cryptocurrency",
  "Other",
];
